<template>
    <div class="product-list">
      <div class="page-header">
        <div>
          <h2 class="page-title">商品列表</h2>
          <p class="page-desc">管理您的团购商品，支持上架/下架、编辑</p>
        </div>
        <el-button type="primary" size="large" @click="$router.push('/product/add')">
          <el-icon><Plus /></el-icon>
          新增商品
        </el-button>
      </div>
      
      <!-- 搜索筛选栏 -->
      <div class="filter-bar">
        <el-input 
          v-model="searchKey" 
          placeholder="搜索商品名称或编号"
          prefix-icon="Search"
          clearable
          style="width: 280px"
          @keyup.enter="handleSearch"
        />
        <el-select v-model="categoryFilter" placeholder="全部分类" clearable style="width: 150px">
          <el-option v-for="cat in categories" :key="cat.id" :label="cat.category_name" :value="cat.id" />
        </el-select>
        <el-select v-model="statusFilter" placeholder="全部状态" clearable style="width: 120px">
          <el-option label="上架中" :value="1" />
          <el-option label="已下架" :value="0" />
        </el-select>
        <el-button type="primary" @click="handleSearch">查询</el-button>
        <el-button @click="resetSearch">重置</el-button>
      </div>
      
      <!-- 统计卡片 -->
      <div class="stat-cards">
        <div class="stat-card">
          <div class="stat-value">{{ pagination.total }}</div>
          <div class="stat-label">总商品数</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ onSaleCount }}</div>
          <div class="stat-label">上架中</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ totalSoldCount }}</div>
          <div class="stat-label">总销量</div>
        </div>
      </div>
      
      <!-- 商品表格 -->
      <el-table :data="productList" border v-loading="loading" style="width: 100%">
        <el-table-column label="商品信息" min-width="300">
          <template #default="{ row }">
            <div class="product-info">
              <el-image :src="row.cover_image || defaultImage" fit="cover" class="product-img" />
              <div class="product-detail">
                <div class="product-name">{{ row.product_name }}</div>
                <div class="product-code">编号：{{ row.product_no }}</div>
                <div class="product-category">分类：{{ row.category_name || '未分类' }}</div>
              </div>
            </div>
          </template>
        </el-table-column>
        
        <el-table-column label="价格" width="160" align="center">
          <template #default="{ row }">
            <div class="price-info">
              <div class="sale-price">¥{{ row.sale_price }}</div>
              <div class="original-price">¥{{ row.original_price }}</div>
              <div class="discount" v-if="row.discount">{{ row.discount }}折</div>
            </div>
          </template>
        </el-table-column>
        
        <el-table-column label="销量/库存" width="140" align="center">
          <template #default="{ row }">
            <div>销量：{{ row.sold_count || 0 }}</div>
            <div>库存：{{ row.stock === -1 ? '不限' : row.stock }}</div>
          </template>
        </el-table-column>
        
        <el-table-column label="有效期" width="200" align="center">
          <template #default="{ row }">
            <span v-if="row.valid_start_date && row.valid_end_date">
              {{ row.valid_start_date }} ~ {{ row.valid_end_date }}
            </span>
            <span v-else class="text-muted">长期有效</span>
          </template>
        </el-table-column>
        
        <el-table-column label="状态" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'" size="small">
              {{ row.status === 1 ? '上架中' : '已下架' }}
            </el-tag>
          </template>
        </el-table-column>
        
        <el-table-column label="排序" width="80" align="center">
          <template #default="{ row }">
            {{ row.sort_order }}
          </template>
        </el-table-column>
        
        <el-table-column label="操作" width="220" fixed="right" align="center">
          <template #default="{ row }">
            <el-button type="primary" link size="small" @click="handleEdit(row)">
              <el-icon><Edit /></el-icon> 编辑
            </el-button>
            <el-button :type="row.status === 1 ? 'warning' : 'success'" link size="small" @click="toggleStatus(row)">
              <el-icon><Switch /></el-icon> {{ row.status === 1 ? '下架' : '上架' }}
            </el-button>
            <el-popconfirm title="确定删除该商品吗？" @confirm="handleDelete(row.id)">
              <template #reference>
                <el-button type="danger" link size="small">
                  <el-icon><Delete /></el-icon> 删除
                </el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页 -->
      <div class="pagination-wrapper">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.limit"
          :total="pagination.total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="fetchData"
          @current-change="fetchData"
        />
      </div>
    </div>
  </template>
  
  <script>
  import { ref, reactive, computed, onMounted } from 'vue'
  import { useRouter } from 'vue-router'
  import { ElMessage, ElLoading } from 'element-plus'
  import Cookies from 'js-cookie'
  import axios from 'axios'
  import config from '@/config'
  import { Plus, Edit, Delete, Switch } from '@element-plus/icons-vue'
  
  export default {
    name: 'ProductListPage',
    components: { Plus, Edit, Delete, Switch },
    setup() {
      const router = useRouter()
      const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
      const agentId = ref(userInfo.agent_id || 0)
      const storeId = ref(userInfo.id)
      
      const loading = ref(false)
      const searchKey = ref('')
      const categoryFilter = ref(null)
      const statusFilter = ref(null)
      const productList = ref([])
      const categories = ref([])
      const defaultImage = 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'
      
      const pagination = reactive({
        page: 1,
        limit: 10,
        total: 0
      })
      
      // 计算属性
      const onSaleCount = computed(() => {
        return productList.value.filter(p => p.status === 1).length
      })
      
      const totalSoldCount = computed(() => {
        return productList.value.reduce((sum, p) => sum + (p.sold_count || 0), 0)
      })
      
      // 获取商品列表
      const fetchData = async () => {
        loading.value = true
        try {
          const params = {
            agent_id: agentId.value,
            store_id: storeId.value,
            page: pagination.page,
            limit: pagination.limit,
            search: searchKey.value,
            category_id: categoryFilter.value,
            status: statusFilter.value
          }
          
          const response = await axios.get(`${config.BASE_URL}/shop/api/product_api.php`, { params })
          
          if (response.data.code === 200) {
            productList.value = response.data.data.list.map(item => ({
              ...item,
              images: item.images ? (typeof item.images === 'string' ? JSON.parse(item.images) : item.images) : [],
              service_features: item.service_features ? (typeof item.service_features === 'string' ? JSON.parse(item.service_features) : item.service_features) : [],
              use_rules: item.use_rules ? (typeof item.use_rules === 'string' ? JSON.parse(item.use_rules) : item.use_rules) : {}
            }))
            pagination.total = response.data.data.pagination.total
          }
        } catch (error) {
          console.error('获取商品列表失败:', error)
          ElMessage.error('获取数据失败')
        } finally {
          loading.value = false
        }
      }
      
      // 获取分类列表
      const fetchCategories = async () => {
        try {
          const response = await axios.get(`${config.BASE_URL}/shop/api/category_api.php`, {
            params: { agent_id: agentId.value, store_id: storeId.value, type: 'product' }
          })
          if (response.data.code === 200) {
            categories.value = response.data.data
          }
        } catch (error) {
          console.error('获取分类失败:', error)
        }
      }
      
      // 搜索
      const handleSearch = () => {
        pagination.page = 1
        fetchData()
      }
      
      // 重置搜索
      const resetSearch = () => {
        searchKey.value = ''
        categoryFilter.value = null
        statusFilter.value = null
        pagination.page = 1
        fetchData()
      }
      
      // 编辑商品
      const handleEdit = (row) => {
        router.push(`/product/edit?id=${row.id}`)
      }
      
      // 切换状态
      const toggleStatus = async (row) => {
        const loadingInstance = ElLoading.service({ text: '处理中...' })
        try {
          const newStatus = row.status === 1 ? 0 : 1
          const response = await axios.put(`${config.BASE_URL}/shop/api/product_api.php`, {
            id: row.id,
            store_id: storeId.value,
            status: newStatus,
            product_name: row.product_name,
            sale_price: row.sale_price
          })
          if (response.data.code === 200) {
            ElMessage.success(newStatus === 1 ? '已上架' : '已下架')
            fetchData()
          } else {
            ElMessage.error(response.data.message || '操作失败')
          }
        } catch (error) {
          console.error('切换状态失败:', error)
          ElMessage.error('操作失败')
        } finally {
          loadingInstance.close()
        }
      }
      
      // 删除商品
      const handleDelete = async (id) => {
        const loadingInstance = ElLoading.service({ text: '删除中...' })
        try {
          const response = await axios.delete(`${config.BASE_URL}/shop/api/product_api.php`, {
            data: { id, store_id: storeId.value }
          })
          if (response.data.code === 200) {
            ElMessage.success('删除成功')
            fetchData()
          } else {
            ElMessage.error(response.data.message || '删除失败')
          }
        } catch (error) {
          console.error('删除失败:', error)
          ElMessage.error('删除失败')
        } finally {
          loadingInstance.close()
        }
      }
      
      onMounted(() => {
        fetchCategories()
        fetchData()
      })
      
      return {
        loading,
        searchKey,
        categoryFilter,
        statusFilter,
        productList,
        categories,
        pagination,
        defaultImage,
        onSaleCount,
        totalSoldCount,
        handleSearch,
        resetSearch,
        handleEdit,
        toggleStatus,
        handleDelete,
        fetchData
      }
    }
  }
  </script>
  
  <style scoped lang="scss">
  .product-list {
    .page-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      
      .page-title {
        font-size: 24px;
        font-weight: 600;
        color: #1a1a2e;
        margin-bottom: 4px;
      }
      
      .page-desc {
        font-size: 14px;
        color: #8c8c8c;
      }
    }
    
    .filter-bar {
      display: flex;
      gap: 12px;
      flex-wrap: wrap;
      margin-bottom: 24px;
    }
    
    .stat-cards {
      display: flex;
      gap: 20px;
      margin-bottom: 24px;
      
      .stat-card {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border-radius: 12px;
        padding: 20px 30px;
        text-align: center;
        min-width: 150px;
        
        &:nth-child(2) {
          background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
        }
        
        &:nth-child(3) {
          background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
        }
        
        .stat-value {
          font-size: 32px;
          font-weight: 700;
          color: #fff;
        }
        
        .stat-label {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.9);
          margin-top: 8px;
        }
      }
    }
    
    .product-info {
      display: flex;
      gap: 12px;
      align-items: center;
      
      .product-img {
        width: 60px;
        height: 60px;
        border-radius: 8px;
        object-fit: cover;
      }
      
      .product-detail {
        .product-name {
          font-weight: 500;
          margin-bottom: 4px;
        }
        
        .product-code, .product-category {
          font-size: 12px;
          color: #8c8c8c;
        }
      }
    }
    
    .price-info {
      .sale-price {
        font-size: 16px;
        font-weight: 600;
        color: #f56c6c;
      }
      
      .original-price {
        font-size: 12px;
        color: #8c8c8c;
        text-decoration: line-through;
      }
      
      .discount {
        font-size: 12px;
        color: #e6a23c;
      }
    }
    
    .text-muted {
      color: #8c8c8c;
      font-size: 12px;
    }
    
    
    .pagination-wrapper {
      display: flex;
      justify-content: flex-end;
      margin-top: 20px;
    }
  }
  </style>